import type React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounceCallback } from 'usehooks-ts';
import useUser from '../../../hooks/useUser';
import { getRecentConversationsRequest } from '../../../redux/ducks/ChatDucks';
import type { ChatConversationDataType } from '../../../redux/ducks/ChatDucks.types';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import Spinner from '../../layout/Spinner/Spinner';
import MenuChatListItem from './MenuChatListItem';

const PRELOADING_THRESHOLD = 0.8;

export type MenuChatsListProps = {
  onChatClick: (id: string) => void;
};

const MenuChatsList: React.FC<MenuChatsListProps> = ({ onChatClick }) => {
  const dispatch = useDispatch();
  const { currentUser } = useUser();

  const conversations = useSelector(
    (state: RootReducerType) => state.chat.conversations,
  );

  const generateConversationBlock = ({
    descriptor,
  }: {
    descriptor: ChatConversationDataType;
  }) => (
    <div
      data-test-id={`menu-chat-list-item-${descriptor.data.id}`}
      key={`menu-chat-list-item-${descriptor.data.id}`}
    >
      <MenuChatListItem data={descriptor.data} onChatClick={onChatClick} />
    </div>
  );

  const fetchMoreData = () => {
    dispatch(
      getRecentConversationsRequest({
        before:
          conversations && conversations.conversations.length > 0
            ? conversations.conversations[
                conversations.conversations.length - 1
              ].data?.last_message?.timestamp || 0
            : 0,
      }),
    );
  };

  const delayedFetchMoreData = useDebounceCallback(() => {
    fetchMoreData();
  }, 1000);

  return currentUser ? (
    <div
      id="scrollableMenuChatsSection"
      className="relative flex size-full max-h-[calc(100dvh-64px)] grow flex-col items-center justify-start gap-0 overflow-auto"
    >
      <InfiniteScroll
        dataLength={conversations ? conversations.conversations.length : 0}
        next={delayedFetchMoreData}
        hasMore={
          conversations
            ? conversations.conversations.length < conversations.total
            : false
        }
        scrollThreshold={PRELOADING_THRESHOLD}
        scrollableTarget="scrollableMenuChatsSection"
        loader={
          <div className="mb-12 flex min-w-full flex-col items-center justify-center p-6">
            <Spinner color="fill-sky-600" />
          </div>
        }
        className="min-w-full"
        style={{
          height: '100%',
          width: '100%',
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '0px',
          marginBottom: '16px',
        }}
      >
        {conversations?.conversations.map(
          (descriptor: ChatConversationDataType) =>
            generateConversationBlock({ descriptor }),
        )}
      </InfiniteScroll>
    </div>
  ) : (
    <div className="flex min-w-full grow flex-col items-center justify-center">
      <Spinner color="fill-sky-600" />
    </div>
  );
};

export default MenuChatsList;
